/**
 * Fiche délibération CNIL
 * Route : /cnil/deliberations/:num
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';

// ── Type ───────────────────────────────────────────────────────────────────────

interface CnilDelib {
  id:             string;
  cnil_id?:       string;
  num:            string;
  nor?:           string;
  nature?:        string;
  nature_delib?:  string;
  etat?:          string;
  titre?:         string;
  titre_long?:    string;
  date_texte?:    string;
  date_publi?:    string;
  extrait?:       string;
  texte_complet?: string;
  has_texte?:     boolean;
  chunk_total?:   number;
  legifrance_url?: string;
}

// Valeurs API du champ nature → clé i18n (cnil.nature.*) ; fallback : valeur brute
const NATURE_KEYS: Record<string, string> = {
  Sanction:       'cnil.nature.sanction',
  Avis:           'cnil.nature.opinion',
  Autorisation:   'cnil.nature.authorization',
  Recommandation: 'cnil.nature.recommendation',
  'Délibération': 'cnil.nature.deliberation',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(d?: string): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
}

function EtatBadge({ etat }: { etat?: string }) {
  const { t } = useTranslation();
  if (!etat) return null;
  const isVigueur = etat === 'VIGUEUR';
  return (
    <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
      isVigueur
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
    }`}>
      {isVigueur ? t('cnil.status.in_force') : t('cnil.status.repealed')}
    </span>
  );
}

function NatureBadge({ nature }: { nature?: string }) {
  const { t } = useTranslation();
  if (!nature) return null;
  const colors: Record<string, string> = {
    Sanction:       'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    Avis:           'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    Autorisation:   'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    Recommandation: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  const cls = colors[nature] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${cls}`}>
      {NATURE_KEYS[nature] ? t(NATURE_KEYS[nature]) : nature}
    </span>
  );
}

// ── Composant principal ────────────────────────────────────────────────────────

export default function CnilFiche() {
  const { t } = useTranslation();
  const { num } = useParams<{ num: string }>();
  const [delib, setDelib]   = useState<CnilDelib | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!num) return;
    setLoading(true);
    fetch(`/api/cnil/deliberations/${encodeURIComponent(num)}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setDelib(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [num]);

  const titre = delib?.titre_long || delib?.titre || t('cnil.fiche.default_title', { num });

  return (
    <>
      <Helmet>
        <title>{t('cnil.fiche.meta_title', { num })}</title>
        <meta name="description" content={delib?.extrait?.slice(0, 160) || ''} />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        <PublicHeader />

        <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">

          {/* Breadcrumb */}
          <nav className="text-xs text-muted-foreground flex items-center gap-1 mb-6">
            <Link to="/" className="hover:underline">{t('cjue.breadcrumb.home')}</Link>
            <span>/</span>
            <Link to="/jurisprudence" className="hover:underline">{t('nav.jurisprudence')}</Link>
            <span>/</span>
            <Link to="/cnil/deliberations" className="hover:underline text-violet-600 dark:text-violet-400">{t('nav.cnil')}</Link>
            <span>/</span>
            <span className="text-gray-500">{num}</span>
          </nav>

          {loading ? (
            <div className="flex justify-center py-24"><Loader /></div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-red-500 mb-4">{t('cnil.fiche.not_found')}</p>
              <Link to="/cnil/deliberations" className="text-sm text-violet-600 hover:underline">
                ← {t('cnil.fiche.back')}
              </Link>
            </div>
          ) : delib && (
            <article>
              {/* ── En-tête ──────────────────────────────────────────────────── */}
              <header className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-6">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-xs font-mono font-bold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 px-2.5 py-1 rounded-lg">
                    🏛️ {t('cnil.card.default_title', { num: delib.num })}
                  </span>
                  <NatureBadge nature={delib.nature_delib ?? delib.nature} />
                  <EtatBadge etat={delib.etat} />
                </div>

                {/* Titre */}
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug mb-4">
                  {titre}
                </h1>

                {/* Méta */}
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                  {delib.date_texte && (
                    <div>
                      <dt className="text-muted-foreground uppercase tracking-widest font-bold mb-0.5">{t('cnil.fiche.date')}</dt>
                      <dd className="text-gray-800 dark:text-gray-200">{formatDate(delib.date_texte)}</dd>
                    </div>
                  )}
                  {delib.date_publi && (
                    <div>
                      <dt className="text-muted-foreground uppercase tracking-widest font-bold mb-0.5">{t('cnil.fiche.publication')}</dt>
                      <dd className="text-gray-800 dark:text-gray-200">{formatDate(delib.date_publi)}</dd>
                    </div>
                  )}
                  {delib.nor && (
                    <div>
                      <dt className="text-muted-foreground uppercase tracking-widest font-bold mb-0.5">{t('cnil.fiche.nor')}</dt>
                      <dd className="font-mono text-gray-700 dark:text-gray-300">{delib.nor}</dd>
                    </div>
                  )}
                  {delib.nature && (
                    <div>
                      <dt className="text-muted-foreground uppercase tracking-widest font-bold mb-0.5">{t('cnil.fiche.nature')}</dt>
                      <dd className="text-gray-800 dark:text-gray-200">{NATURE_KEYS[delib.nature] ? t(NATURE_KEYS[delib.nature]) : delib.nature}</dd>
                    </div>
                  )}
                </dl>

                {/* Lien Légifrance */}
                {delib.legifrance_url && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <a
                      href={delib.legifrance_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 underline underline-offset-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {t('cnil.fiche.see_legifrance')} ↗
                    </a>
                  </div>
                )}
              </header>

              {/* ── Extrait ──────────────────────────────────────────────────── */}
              {delib.extrait && (
                <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-6">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                    {t('cnil.fiche.summary')}
                  </h2>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {delib.extrait}
                  </p>
                </section>
              )}

              {/* ── Texte intégral ───────────────────────────────────────────── */}
              {delib.texte_complet ? (
                <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      {t('cnil.fiche.full_text')}
                    </h2>
                    {(delib.chunk_total ?? 1) > 1 && (
                      <span className="text-[10px] text-muted-foreground bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                        {t('cnil.fiche.fragmented', { count: delib.chunk_total })}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {delib.texte_complet}
                  </div>
                </section>
              ) : (
                <section className="bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-6 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('cnil.fiche.full_text_unavailable')}
                  </p>
                  {delib.legifrance_url && (
                    <a
                      href={delib.legifrance_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {t('cnil.fiche.consult_legifrance')} ↗
                    </a>
                  )}
                </section>
              )}

              {/* ── Lien jurisprudence liée ───────────────────────────────────── */}
              <section className="bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800 rounded-2xl p-5 text-center">
                <p className="text-sm text-violet-800 dark:text-violet-300 mb-3">
                  🔗 {t('cnil.fiche.cited_text')}
                </p>
                <Link
                  to={`/jurisprudence/rgpd?q=délibération+CNIL+${encodeURIComponent(delib.num)}`}
                  className="text-sm font-semibold text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-200 underline underline-offset-2"
                >
                  {t('cnil.fiche.search_citing')} →
                </Link>
              </section>

              {/* ── Retour ───────────────────────────────────────────────────── */}
              <div className="mt-6 text-center">
                <Link
                  to="/cnil/deliberations"
                  className="text-sm text-muted-foreground hover:text-gray-900 dark:hover:text-gray-100 underline underline-offset-2"
                >
                  ← {t('cnil.fiche.back')}
                </Link>
              </div>
            </article>
          )}
        </main>

        <PublicFooter />
      </div>
    </>
  );
}
